import React, {useMemo} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {p, pb, ph, pl, pv} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";

import {ApplicationBox} from "../../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {RPModal} from "../../../atoms/RPModal";
import {PropertyPlanDisplay} from "../../../property/components/PropertyPlanDisplay";
import {PropertyPlanModalContent} from "../../../property/components/PropertyPlanModalContent";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {generateHash} from "../../../utils/generate_hash";
import {GalleryModalTabs} from "../../atoms/GalleryModalTabs";
import {OfferMapModalContent} from "../../detail/components/OfferMapModalContent";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {PlanSelectModal} from "../plan_select_modal/PlanSelectModal";
import {ConstructionGalleryPlaceholder} from "./ConstructionGalleryPlaceholder";
import {GalleryHolderPropertyPlanButton} from "./GalleryHolderPropertyPlanButton";
import {OfferConstructionGalleryTile} from "./OfferConstructionGalleryTile";
import {OfferGalleryModalContent} from "./OfferGalleryModalContent";

interface IProps {
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    property?: IPropertyDetail;
    galleryPictures?: IOfferGallery | null;
    propertyGalleryPictures?: IPropertyGallery | null;
    constructionGalleryPictures?: IConstructionGalleryResponse | null;
    offerBuildings?: IOfferBuilding[];
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    onModalClose?: () => void;
    isOpen: boolean;
}

export const GalleryHolderModal = (props: IProps) => {
    const {offer, property, handleGalleryModalChange, modalState, onModalClose, offerBuildings} = props;
    const storeHash = useMemo(() => generateHash(), []);
    const [showPlanSelect, setShowPlanSelect] = React.useState(true);
    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isLimited && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property?.for_sale;
    const isMapsTabOpen = props.modalState === OfferModalActiveTab.MAP;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        property: property && {
            area: property.area,
            discount: property.discount,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };

    const onClose = () => {
        onModalClose && onModalClose();
    };

    const onPlanButtonClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
    };

    const lastGalleryTile = props.constructionGalleryPictures?.length ? null : <OfferConstructionGalleryTile onButtonClick={handleGalleryModalChange} />;

    const renderModalContent = () => {
        switch (modalState) {
            case OfferModalActiveTab.PLAN:
                return (
                    property && (
                        <PropertyPlanDisplay
                            property={property}
                            vendor={offer.vendor}
                            onPlanDownloadClick={onPlanButtonClick}
                            legacyPlan={<PropertyPlanModalContent offerName={offer.name} property={property} onPlanButtonClick={onPlanButtonClick} />}
                        />
                    )
                );
            case OfferModalActiveTab.GALLERY:
                return (
                    <OfferGalleryModalContent
                        key="offerGallery"
                        offer={offer}
                        offerGallery={props.galleryPictures ?? []}
                        propertyGallery={props.propertyGalleryPictures ?? []}
                        constructionGallery={props.constructionGalleryPictures ?? []}
                        showMainImage
                        lastGalleryTile={lastGalleryTile}
                    />
                );
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                return props.constructionGalleryPictures && props.constructionGalleryPictures.length > 0 ? (
                    <OfferGalleryModalContent key="offerConstructionGallery" offer={offer} constructionGallery={props.constructionGalleryPictures ?? []} />
                ) : (
                    <ConstructionGalleryPlaceholder />
                );
            case OfferModalActiveTab.MAP:
                return (
                    <OfferMapModalContent
                        offer={{
                            ...offer,
                            geo_area: {
                                coordinates: {
                                    coordinates: offer.geo_area.coordinates
                                }
                            }
                        }}
                        offerBuildings={offerBuildings}
                        property={property}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <GalleryModal
                isOpen={props.isOpen}
                onModalClose={onClose}
                closeButton={(closeModal) => <CloseIcon onClick={closeModal} fill="#fff" size="2" css={modalCloseIconHolder} />}
                isMapsTabOpen={isMapsTabOpen}
            >
                <div css={galleryModalImmediateWrapper}>
                    <ModalHeader css={isMapsTabOpen && mapsTabOpenHeaderStyle}>
                        <GalleryModalTabs
                            hasPlanImage={!isEmpty(property?.plan_image_pages)}
                            handleGalleryModalChange={handleGalleryModalChange}
                            modalState={props.modalState || undefined}
                        />
                    </ModalHeader>

                    <div css={galleryModalContent}>
                        {renderModalContent()}

                        <div css={galleryModalFormHolder}>
                            <Text variant="headline_4" as="h1" css={offerInfoTextStyle} mb="1.6rem">
                                <span className="bt">{offer.name}</span>
                                {props.property && ` - ${isApartment ? "Apartament" : getOfferTypeNameCapital(offer.type)} - ${props.property.number}`}
                            </Text>
                            <Text variant="info_txt_1" as="h2" css={offerInfoTextStyle} mb="1.6rem" className="bt">
                                {offer.address}
                            </Text>

                            {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                                <ApplicationBox
                                    storeHash={storeHash}
                                    source={props.source}
                                    sourceSection={modalState === OfferModalActiveTab.MAP ? ApplicationSourceSection.MAP : ApplicationSourceSection.GALLERY}
                                    vendorId={props.offer.vendor.id}
                                    offerId={props.offer.id}
                                    propertyId={props.property?.id}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    optionalVendorInfoData={offer.vendor}
                                    optionalOfferPhoneData={offer}
                                    formButtonText="Zapytaj dewelopera"
                                    sidebarBg="dark"
                                />
                            )}
                            {property && property.plan && (
                                <GalleryHolderPropertyPlanButton onPlanButtonClick={onPlanButtonClick} propertyPlan={property.plan} />
                            )}
                        </div>
                    </div>

                    {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                        <ContactBar
                            css={[contactBarStyle, isMapsTabOpen && contactBarWithMapsTabOpenStyle]}
                            salesOffices={offer.sales_offices}
                            vendor={offer.vendor}
                            trackVendor={trackedData.vendor}
                            trackOffer={trackedData.offer}
                            trackProperty={trackedData.property}
                            phoneClip={offer.configuration.phone_clip}
                            isPhoneClipEnabled={offer.is_phone_clip_enabled}
                            backgroundColor="#20212d"
                            source={props.source}
                            isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                            offer={offer}
                            propertyPrice={property?.price}
                            buttonsSize="x-small"
                        />
                    )}
                </div>
            </GalleryModal>

            <PlanSelectModal isOpen={modalState === OfferModalActiveTab.PLAN && showPlanSelect} onClose={() => setShowPlanSelect(false)} />
        </>
    );
};

const galleryModalImmediateWrapper = css`
    display: flex;
    flex: 100%;
    flex-direction: column;
`;

const mapsTabOpenHeaderStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pb(0)};
        ${ph(0)};
    }
`;

const galleryModalContent = css`
    flex: 1 1 100%;
    display: flex;
    flex-direction: row;
    overflow: auto;
    position: relative;

    @media (min-width: ${getThemeBreakpoint().md}) {
        padding-bottom: 0;
    }
`;

const offerInfoTextStyle = css`
    color: #fff;
`;
const modalCloseIconHolder = css`
    position: absolute;
    right: 1rem;
    top: 1rem;
    cursor: pointer;

    @media (min-width: ${getThemeBreakpoint().md}) {
        right: 3rem;
        top: 3rem;
    }
`;

interface GalleryModalProps {
    isMapsTabOpen?: boolean;
}

const GalleryModal = styled(RPModal)<GalleryModalProps>`
    ${({isMapsTabOpen}) => p(isMapsTabOpen ? 0 : 0.5)}
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${(props) => props.theme.colors.secondary};
    border-radius: 0;

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)}
    }
`;

const ModalHeader = styled.div`
    flex: 1 0 auto;
    color: white;
    padding: 2rem 0 2.4rem 0;
    display: flex;
    flex-direction: column;

    @media (min-width: ${getThemeBreakpoint().md}) {
        padding: 2rem 5rem 2.8rem 3rem;
        flex-direction: row;
        flex-wrap: wrap;
    }
`;

const galleryModalFormHolder = (theme: Theme) => css`
    flex-grow: 0;
    flex-shrink: 0;
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pl(3)}
        display: flex;
        flex-direction: column;
        flex-basis: 33rem;
    }
`;

const contactBarStyle = css`
    position: relative;
    ${pv(1)};
    ${ph(1)};
`;

const contactBarWithMapsTabOpenStyle = css`
    ${ph(2)};
`;
import React, {useMemo} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {p, pb, ph, pl, pv} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";

import {ApplicationBox} from "../../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {RPModal} from "../../../atoms/RPModal";
import {PropertyPlanDisplay} from "../../../property/components/PropertyPlanDisplay";
import {PropertyPlanModalContent} from "../../../property/components/PropertyPlanModalContent";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {generateHash} from "../../../utils/generate_hash";
import {GalleryModalTabs} from "../../atoms/GalleryModalTabs";
import {OfferMapModalContent} from "../../detail/components/OfferMapModalContent";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {PlanSelectModal} from "../plan_select_modal/PlanSelectModal";
import {ConstructionGalleryPlaceholder} from "./ConstructionGalleryPlaceholder";
import {GalleryHolderPropertyPlanButton} from "./GalleryHolderPropertyPlanButton";
import {OfferConstructionGalleryTile} from "./OfferConstructionGalleryTile";
import {OfferGalleryModalContent} from "./OfferGalleryModalContent";

interface IProps {
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    property?: IPropertyDetail;
    galleryPictures?: IOfferGallery | null;
    propertyGalleryPictures?: IPropertyGallery | null;
    constructionGalleryPictures?: IConstructionGalleryResponse | null;
    offerBuildings?: IOfferBuilding[];
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    onModalClose?: () => void;
    isOpen: boolean;
}

export const GalleryHolderModal = (props: IProps) => {
    const {offer, property, handleGalleryModalChange, modalState, onModalClose, offerBuildings} = props;
    const storeHash = useMemo(() => generateHash(), []);
    const [showPlanSelect, setShowPlanSelect] = React.useState(true);
    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isLimited && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property?.for_sale;
    const isMapsTabOpen = props.modalState === OfferModalActiveTab.MAP;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        property: property && {
            area: property.area,
            discount: property.discount,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };

    const onClose = () => {
        onModalClose && onModalClose();
    };

    const onPlanButtonClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
    };

    const lastGalleryTile = props.constructionGalleryPictures?.length ? null : <OfferConstructionGalleryTile onButtonClick={handleGalleryModalChange} />;

    const renderModalContent = () => {
        switch (modalState) {
            case OfferModalActiveTab.PLAN:
                return (
                    property && (
                        <PropertyPlanDisplay
                            property={property}
                            vendor={offer.vendor}
                            onPlanDownloadClick={onPlanButtonClick}
                            legacyPlan={<PropertyPlanModalContent offerName={offer.name} property={property} onPlanButtonClick={onPlanButtonClick} />}
                        />
                    )
                );
            case OfferModalActiveTab.GALLERY:
                return (
                    <OfferGalleryModalContent
                        key="offerGallery"
                        offer={offer}
                        offerGallery={props.galleryPictures ?? []}
                        propertyGallery={props.propertyGalleryPictures ?? []}
                        constructionGallery={props.constructionGalleryPictures ?? []}
                        showMainImage
                        lastGalleryTile={lastGalleryTile}
                    />
                );
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                return props.constructionGalleryPictures && props.constructionGalleryPictures.length > 0 ? (
                    <OfferGalleryModalContent key="offerConstructionGallery" offer={offer} constructionGallery={props.constructionGalleryPictures ?? []} />
                ) : (
                    <ConstructionGalleryPlaceholder />
                );
            case OfferModalActiveTab.MAP:
                return (
                    <OfferMapModalContent
                        offer={{
                            ...offer,
                            geo_area: {
                                coordinates: {
                                    coordinates: offer.geo_area.coordinates
                                }
                            }
                        }}
                        offerBuildings={offerBuildings}
                        property={property}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <GalleryModal
                isOpen={props.isOpen}
                onModalClose={onClose}
                closeButton={(closeModal) => <CloseIcon onClick={closeModal} fill="#fff" size="2" css={modalCloseIconHolder} />}
                isMapsTabOpen={isMapsTabOpen}
            >
                <div css={galleryModalImmediateWrapper}>
                    <ModalHeader css={isMapsTabOpen && mapsTabOpenHeaderStyle}>
                        <GalleryModalTabs
                            hasPlanImage={!isEmpty(property?.plan_image_pages)}
                            handleGalleryModalChange={handleGalleryModalChange}
                            modalState={props.modalState || undefined}
                        />
                    </ModalHeader>

                    <div css={galleryModalContent}>
                        {renderModalContent()}

                        <div css={galleryModalFormHolder}>
                            <Text variant="headline_4" as="h1" css={offerInfoTextStyle} mb="1.6rem">
                                <span className="bt">{offer.name}</span>
                                {props.property && ` - ${isApartment ? "Apartament" : getOfferTypeNameCapital(offer.type)} - ${props.property.number}`}
                            </Text>
                            <Text variant="info_txt_1" as="h2" css={offerInfoTextStyle} mb="1.6rem" className="bt">
                                {offer.address}
                            </Text>

                            {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                                <ApplicationBox
                                    storeHash={storeHash}
                                    source={props.source}
                                    sourceSection={modalState === OfferModalActiveTab.MAP ? ApplicationSourceSection.MAP : ApplicationSourceSection.GALLERY}
                                    vendorId={props.offer.vendor.id}
                                    offerId={props.offer.id}
                                    propertyId={props.property?.id}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    optionalVendorInfoData={offer.vendor}
                                    optionalOfferPhoneData={offer}
                                    formButtonText="Zapytaj dewelopera"
                                    sidebarBg="dark"
                                />
                            )}
                            {property && property.plan && (
                                <GalleryHolderPropertyPlanButton onPlanButtonClick={onPlanButtonClick} propertyPlan={property.plan} />
                            )}
                        </div>
                    </div>

                    {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                        <ContactBar
                            css={[contactBarStyle, isMapsTabOpen && contactBarWithMapsTabOpenStyle]}
                            salesOffices={offer.sales_offices}
                            vendor={offer.vendor}
                            trackVendor={trackedData.vendor}
                            trackOffer={trackedData.offer}
                            trackProperty={trackedData.property}
                            phoneClip={offer.configuration.phone_clip}
                            isPhoneClipEnabled={offer.is_phone_clip_enabled}
                            backgroundColor="#20212d"
                            source={props.source}
                            isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                            offer={offer}
                            propertyPrice={property?.price}
                            buttonsSize="x-small"
                        />
                    )}
                </div>
            </GalleryModal>

            <PlanSelectModal isOpen={modalState === OfferModalActiveTab.PLAN && showPlanSelect} onClose={() => setShowPlanSelect(false)} />
        </>
    );
};

const galleryModalImmediateWrapper = css`
    display: flex;
    flex: 100%;
    flex-direction: column;
`;

const mapsTabOpenHeaderStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pb(0)};
        ${ph(0)};
    }
`;

const galleryModalContent = css`
    flex: 1 1 100%;
    display: flex;
    flex-direction: row;
    overflow: auto;
    position: relative;

    @media (min-width: ${getThemeBreakpoint().md}) {
        padding-bottom: 0;
    }
`;

const offerInfoTextStyle = css`
    color: #fff;
`;
const modalCloseIconHolder = css`
    position: absolute;
    right: 1rem;
    top: 1rem;
    cursor: pointer;

    @media (min-width: ${getThemeBreakpoint().md}) {
        right: 3rem;
        top: 3rem;
    }
`;

interface GalleryModalProps {
    isMapsTabOpen?: boolean;
}

const GalleryModal = styled(RPModal)<GalleryModalProps>`
    ${({isMapsTabOpen}) => p(isMapsTabOpen ? 0 : 0.5)}
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${(props) => props.theme.colors.secondary};
    border-radius: 0;

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)}
    }
`;

const ModalHeader = styled.div`
    flex: 1 0 auto;
    color: white;
    padding: 2rem 0 2.4rem 0;
    display: flex;
    flex-direction: column;

    @media (min-width: ${getThemeBreakpoint().md}) {
        padding: 2rem 5rem 2.8rem 3rem;
        flex-direction: row;
        flex-wrap: wrap;
    }
`;

const galleryModalFormHolder = (theme: Theme) => css`
    flex-grow: 0;
    flex-shrink: 0;
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pl(3)}
        display: flex;
        flex-direction: column;
        flex-basis: 33rem;
    }
`;

const contactBarStyle = css`
    position: relative;
    ${pv(1)};
    ${ph(1)};
`;

const contactBarWithMapsTabOpenStyle = css`
    ${ph(2)};
`;
